import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldWarning, ArrowsClockwise, Wallet, Sparkle, Robot, TrendDown,
  TrendUp, Info, Camera, CheckCircle,
} from "@phosphor-icons/react";

/**
 * AdminLLMUsagePage
 *
 * Live "where is the Universal Key spend going?" dashboard. Every LlmChat
 * call the app makes is auto-logged to `db.llm_usage_ledger` by
 * `backend/llm_meter.py`. This page aggregates that stream.
 *
 * NOTE — Emergent does not expose a public balance API. The admin pastes
 * the current balance in USD from their Emergent profile page; we snapshot
 * it and DISPLAY everything in ₹ (USD→INR rate configurable via the
 * `USD_TO_INR_RATE` env var, default 84).
 */

const WINDOWS = [
  { key: "1h",  label: "Last 1 h" },
  { key: "24h", label: "Last 24 h" },
  { key: "7d",  label: "Last 7 d" },
  { key: "30d", label: "Last 30 d" },
  { key: "all", label: "All time" },
];

const FEATURE_LABELS = {
  ai_astrologer_chat:    "AI Astrologer Chat",
  autoblog_plan:         "AutoBlog · Plan",
  autoblog_write:        "AutoBlog · Write",
  autoblog_image:        "AutoBlog · Hero Image (Nano Banana)",
  autoblog_rewrite:      "AutoBlog · Rewrite",
  autoblog_edit:         "AutoBlog · Editor",
  autoblog_other:        "AutoBlog · Other",
  twenty_year_report:    "20-Year Prediction Report",
  life_replay:           "Life Replay",
  palm_reading:          "Palm Reading",
  varshaphal_narrative:  "Varshaphal Narrative",
  financial_forecast:    "Financial Astro Forecast",
  d11_wealth_report:     "D11 Rudramsha Wealth Report",
  event_predictions:     "Personalized Event Predictions",
  daily_horoscope:       "Daily Horoscope",
  low_review_debug:      "Low-Review Debugger (Admin)",
  nadi_deepdive_batch:   "Nadi Deep-Dive · Batch",
  nadi_deepdive_meta:    "Nadi Deep-Dive · Meta",
  nadi_deepdive_transit: "Nadi Deep-Dive · Transit",
  celebrity_ai_reader:   "Celebrity AI Reader",
  unknown:               "Unknown / Other",
};

const FEATURE_TONE = {
  ai_astrologer_chat:  "bg-sky-100 text-sky-800 border-sky-300",
  autoblog_write:      "bg-violet-100 text-violet-800 border-violet-300",
  autoblog_plan:       "bg-violet-100 text-violet-800 border-violet-300",
  autoblog_image:      "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  autoblog_rewrite:    "bg-violet-100 text-violet-800 border-violet-300",
  autoblog_edit:       "bg-violet-100 text-violet-800 border-violet-300",
  daily_horoscope:     "bg-amber-100 text-amber-800 border-amber-300",
  twenty_year_report:  "bg-emerald-100 text-emerald-800 border-emerald-300",
  life_replay:         "bg-teal-100 text-teal-800 border-teal-300",
  palm_reading:        "bg-orange-100 text-orange-800 border-orange-300",
  financial_forecast:  "bg-lime-100 text-lime-800 border-lime-300",
  celebrity_ai_reader: "bg-rose-100 text-rose-800 border-rose-300",
};

function labelForFeature(k) { return FEATURE_LABELS[k] || (k || "unknown"); }
function toneForFeature(k) { return FEATURE_TONE[k] || "bg-stone-100 text-stone-700 border-stone-300"; }

function fmtUsd(n, digits = 4) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toFixed(digits)}`;
}
function fmtInr(n, digits = 2) {
  if (n == null || isNaN(n)) return "—";
  // Indian locale grouping (1,23,456.78)
  return `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })}`;
}
function fmtInt(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }); } catch { return iso; }
}
function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = (now - then) / 60000;
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${Math.round(diffMin)} m ago`;
    if (diffMin < 60 * 24) return `${(diffMin / 60).toFixed(1)} h ago`;
    return `${(diffMin / (60 * 24)).toFixed(1)} d ago`;
  } catch { return ""; }
}

export default function AdminLLMUsagePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [window, setWindow] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showSnap, setShowSnap] = useState(false);
  const [snapValue, setSnapValue] = useState("");
  const [snapNote, setSnapNote] = useState("");
  const [savingSnap, setSavingSnap] = useState(false);

  const isSuperAdmin = !!user?.is_super_admin;

  const refresh = useCallback(async (silent = false) => {
    if (!isSuperAdmin) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/llm-usage", {
        params: { window, limit_recent: 60 },
      });
      setData(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load usage";
      setError(msg);
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, window]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => refresh(true), 15000);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  const saveSnapshot = async () => {
    const v = parseFloat(snapValue);
    if (!Number.isFinite(v) || v < 0) { toast.error("Enter a valid USD balance."); return; }
    setSavingSnap(true);
    try {
      await api.post("/admin/llm-usage/balance-snapshot", {
        balance_usd: v,
        note: snapNote.trim(),
      });
      toast.success(`Balance snapshot saved at $${v.toFixed(4)} (₹${(v * (data?.currency?.usd_to_inr_rate || 84)).toFixed(2)})`);
      setSnapValue(""); setSnapNote(""); setShowSnap(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Snapshot failed");
    } finally {
      setSavingSnap(false);
    }
  };

  const maxFeatureCost = useMemo(() => {
    if (!data?.by_feature?.length) return 0;
    return Math.max(...data.by_feature.map((f) => f.cost_inr || 0));
  }, [data]);

  if (!isSuperAdmin) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="llm-usage-denied">
          <ShieldWarning size={48} weight="duotone" className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif-display text-stone-800 mb-2">Super-admin only</h1>
          <p className="text-stone-600">This dashboard shows where the Emergent Universal Key is being spent across the app.</p>
          <button onClick={() => navigate("/")} className="mt-6 px-5 py-2 rounded-full bg-stone-800 text-white hover:bg-stone-900" data-testid="back-home">
            Back to home
          </button>
        </div>
        <Footer />
      </PageShell>
    );
  }

  const t = data?.totals || {};
  const snap = data?.balance_snapshot;
  const burnUsd = data?.burn_since_snapshot_usd;
  const burnInr = data?.burn_since_snapshot_inr;
  const rate = data?.currency?.usd_to_inr_rate || 84;
  const impliedBalanceUsd = snap ? Math.max(0, snap.balance_usd - (burnUsd || 0)) : null;
  const impliedBalanceInr = impliedBalanceUsd != null ? impliedBalanceUsd * rate : null;

  return (
    <PageShell>
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-llm-usage-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Super Admin · Real-time</div>
            <h1 className="text-3xl sm:text-4xl font-serif-display text-stone-800">Universal Key spend</h1>
            <p className="text-stone-600 mt-1 text-sm max-w-2xl">
              Every LLM call the app makes — text, image, video — auto-logged with model, tokens, latency and estimated cost. Use it to spot where the Universal Key is bleeding.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer" data-testid="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-stone-300"
              />
              Auto-refresh (15s)
            </label>
            <button
              onClick={() => refresh()}
              disabled={loading}
              className="rounded-full bg-stone-800 text-white text-sm px-4 py-1.5 inline-flex items-center gap-1.5 hover:bg-stone-900 disabled:opacity-60"
              data-testid="refresh-btn"
            >
              <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Window selector */}
        <div className="mt-6 flex gap-2 flex-wrap" data-testid="window-selector">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWindow(w.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm border transition
                ${window === w.key
                  ? "bg-stone-800 text-white border-stone-800"
                  : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
              data-testid={`window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="llm-usage-error">
            {error}
          </div>
        )}

        {/* KPI cards */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KpiCard label="Total spend" value={fmtInr(t.cost_inr, 2)} sub={`${fmtInt(t.calls)} calls · ≈ ${fmtUsd(t.cost_usd, 4)}`} highlight testId="kpi-cost" />
          <KpiCard label="Tokens in / out"
                   value={`${fmtInt(t.input_tokens)} · ${fmtInt(t.output_tokens)}`}
                   sub="est. (chars ÷ 4)"
                   testId="kpi-tokens" />
          <KpiCard label="Images generated" value={fmtInt(t.images)} sub="Nano Banana" testId="kpi-images" />
          <KpiCard label="Avg latency" value={`${fmtInt(t.avg_latency_ms)} ms`}
                   sub={t.errors > 0 ? `${t.errors} errors` : "no errors"}
                   error={t.errors > 0}
                   testId="kpi-latency" />
        </div>

        {/* Balance snapshot */}
        <section className="mt-6 rounded-2xl border border-amber-300 bg-amber-50/60 p-5" data-testid="balance-snapshot-panel">
          <div className="flex items-start gap-3 flex-wrap">
            <Wallet size={22} weight="duotone" className="text-amber-800 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-stone-900 font-medium">Emergent Universal Key balance</div>
              <div className="text-stone-600 text-sm mt-0.5">
                Emergent doesn&apos;t expose a public balance API, so paste your current balance from
                {" "}<a href="https://emergent.sh/profile" target="_blank" rel="noreferrer" className="underline">emergent.sh/profile</a>
                {" "}(Profile → Universal Key) and we&apos;ll compute burn since then.
              </div>

              {snap && (
                <div className="mt-3 flex flex-wrap gap-6 text-sm" data-testid="snapshot-summary">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Snapshot</div>
                    <div className="text-stone-900 font-semibold">{fmtInr(snap.balance_inr, 2)}</div>
                    <div className="text-[11px] text-stone-500">≈ {fmtUsd(snap.balance_usd, 2)} · {fmtDate(snap.saved_at)} · {fmtRelative(snap.saved_at)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Burn since snapshot</div>
                    <div className="text-rose-700 font-semibold">− {fmtInr(burnInr, 2)}</div>
                    <div className="text-[11px] text-stone-500">est. · ≈ {fmtUsd(burnUsd, 4)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Implied balance now</div>
                    <div className="font-serif-display text-2xl text-emerald-700">{fmtInr(impliedBalanceInr, 2)}</div>
                    <div className="text-[11px] text-stone-500">≈ {fmtUsd(impliedBalanceUsd, 2)} · = snapshot − burn</div>
                  </div>
                  {snap.note && (
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-stone-500">Note</div>
                      <div className="text-stone-700 text-sm italic">&ldquo;{snap.note}&rdquo;</div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-2 text-[11px] text-stone-500">
                Rate: <span className="font-mono">₹{rate.toFixed(2)}</span> / USD (override via <code>USD_TO_INR_RATE</code> env)
              </div>
            </div>
            <button
              onClick={() => setShowSnap(true)}
              className="rounded-full bg-amber-700 hover:bg-amber-800 text-white text-sm px-4 py-1.5 inline-flex items-center gap-1.5"
              data-testid="snapshot-btn"
            >
              <Camera size={14} /> {snap ? "Update snapshot" : "Save snapshot"}
            </button>
          </div>
        </section>

        {showSnap && (
          <SnapshotModal
            value={snapValue} onChange={setSnapValue}
            note={snapNote} onNoteChange={setSnapNote}
            saving={savingSnap}
            onCancel={() => setShowSnap(false)}
            onSave={saveSnapshot}
          />
        )}

        {/* Two-column: by-feature bar chart + by-model table */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-5" data-testid="by-feature-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-serif-display text-stone-800 flex items-center gap-2">
                  <Sparkle size={18} weight="fill" className="text-amber-500" /> Spend by feature
                </h2>
                <div className="text-[11px] text-stone-500">Where the Universal Key is being burned across the app.</div>
              </div>
              <div className="text-[11px] text-stone-500 uppercase tracking-wider">{data?.by_feature?.length || 0} tags</div>
            </div>
            {!data?.by_feature?.length ? (
              <EmptyState label="No LLM calls in this window yet — trigger some usage and refresh." />
            ) : (
              <ul className="space-y-2.5">
                {data.by_feature.map((f) => (
                  <FeatureBar key={f.feature} f={f} max={maxFeatureCost} totalCost={t.cost_inr} />
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="by-model-panel">
            <h2 className="text-xl font-serif-display text-stone-800 flex items-center gap-2">
              <Robot size={18} weight="fill" className="text-violet-600" /> Spend by model
            </h2>
            <div className="text-[11px] text-stone-500 mb-3">Which model families cost the most.</div>
            {!data?.by_model?.length ? (
              <EmptyState label="No calls yet." />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left pb-1 font-medium">Model</th>
                    <th className="text-right pb-1 font-medium">Calls</th>
                    <th className="text-right pb-1 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.by_model.map((m) => (
                    <tr key={`${m.provider}-${m.model}`} data-testid={`model-row-${m.model}`}>
                      <td className="py-1.5 pr-2 truncate max-w-[190px]" title={m.model}>
                        <div className="text-stone-900 truncate">{m.model}</div>
                        <div className="text-[10px] text-stone-500">{m.provider}</div>
                      </td>
                      <td className="py-1.5 text-right text-stone-700">{fmtInt(m.calls)}</td>
                      <td className="py-1.5 text-right text-rose-700 font-semibold">{fmtInr(m.cost_inr, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>

        {/* Recent calls table */}
        <section className="mt-6 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="recent-panel">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="text-xl font-serif-display text-stone-800">Recent LLM calls</h2>
            <div className="text-[11px] text-stone-500 uppercase tracking-wider">
              {data?.recent?.length || 0} shown
            </div>
          </div>
          {!data?.recent?.length ? (
            <div className="p-10"><EmptyState label="No calls in this window." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="recent-table">
                <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">When</th>
                    <th className="text-left px-4 py-2 font-medium">Feature</th>
                    <th className="text-left px-4 py-2 font-medium">Model</th>
                    <th className="text-right px-4 py-2 font-medium">Tokens</th>
                    <th className="text-right px-4 py-2 font-medium">Latency</th>
                    <th className="text-right px-4 py-2 font-medium">Cost</th>
                    <th className="text-center px-4 py-2 font-medium">OK</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.recent.map((r, i) => (
                    <tr key={r.id || i} data-testid={`recent-row-${i}`}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-stone-800">{fmtDate(r.ts)}</div>
                        <div className="text-[10px] text-stone-500">{fmtRelative(r.ts)}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wider ${toneForFeature(r.feature)}`}>
                          {labelForFeature(r.feature)}
                        </span>
                        {r.n_images > 0 && (
                          <span className="ml-1 text-[10px] text-fuchsia-700">🖼️{r.n_images}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-stone-900">{r.model}</div>
                        <div className="text-[10px] text-stone-500">{r.provider}</div>
                      </td>
                      <td className="px-4 py-2 text-right text-stone-700 whitespace-nowrap">
                        {fmtInt(r.est_input_tokens)} · {fmtInt(r.est_output_tokens)}
                      </td>
                      <td className="px-4 py-2 text-right text-stone-700 whitespace-nowrap">
                        {fmtInt(r.latency_ms)} ms
                      </td>
                      <td className="px-4 py-2 text-right text-rose-700 font-semibold whitespace-nowrap">
                        {fmtInr(r.est_cost_inr, 4)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {r.ok
                          ? <CheckCircle size={16} weight="fill" className="text-emerald-600 inline" />
                          : <span className="text-[10px] text-rose-700" title={r.error || ""}>ERR</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="mt-6 text-[11px] text-stone-500 flex items-start gap-2" data-testid="disclaimer">
          <Info size={14} className="mt-0.5 shrink-0" />
          <p>
            Cost estimates are heuristic. Tokens ≈ characters ÷ 4. Prices from published per-model rates.
            Real billing is settled by Emergent server-side and may differ by ≈10-15%. This dashboard
            shows spend for calls made <em>since</em> instrumentation shipped — earlier calls are not in the ledger.
          </p>
        </div>
      </main>
      <Footer />
    </PageShell>
  );
}

function KpiCard({ label, value, sub, highlight, error, testId }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        highlight ? "border-amber-300 bg-amber-50" :
        error     ? "border-rose-300 bg-rose-50" :
                    "border-stone-200 bg-white"
      }`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-serif-display text-2xl mt-0.5 ${highlight ? "text-amber-800" : "text-stone-900"}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function FeatureBar({ f, max, totalCost }) {
  const pct = max > 0 ? Math.min(100, (f.cost_inr / max) * 100) : 0;
  const sharePct = totalCost > 0 ? Math.round((f.cost_inr / totalCost) * 100) : 0;
  const tone = toneForFeature(f.feature);
  return (
    <li className="text-sm" data-testid={`feature-bar-${f.feature}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wider ${tone}`}>
            {labelForFeature(f.feature)}
          </span>
          <span className="text-[11px] text-stone-500">
            {fmtInt(f.calls)} calls · {fmtInt(f.tokens)} tok
            {f.images > 0 ? ` · ${f.images} img` : ""}
            {f.errors > 0 ? ` · ${f.errors} err` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-rose-700 font-semibold text-sm">{fmtInr(f.cost_inr, 2)}</span>
          <span className="text-[10px] text-stone-500 w-8 text-right">{sharePct}%</span>
        </div>
      </div>
      <div className="mt-1 h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-amber-500 via-rose-500 to-red-600" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center py-8 text-stone-500 text-sm">
      <TrendUp size={20} weight="duotone" className="mx-auto mb-2 text-stone-400" />
      {label}
    </div>
  );
}

function SnapshotModal({ value, onChange, note, onNoteChange, saving, onCancel, onSave }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="snapshot-modal"
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-serif-display text-stone-900 flex items-center gap-2">
          <Wallet size={20} weight="duotone" className="text-amber-700" /> Save balance snapshot
        </h3>
        <p className="text-sm text-stone-600 mt-1">
          Open <a href="https://emergent.sh/profile" target="_blank" rel="noreferrer" className="underline">emergent.sh/profile</a> → Universal Key → copy your remaining balance in USD.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Balance (USD)</label>
            <input
              type="number" step="0.01" min="0"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="e.g. 42.50"
              className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-stone-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              data-testid="snapshot-input-balance"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="e.g. after topping up $20"
              className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-stone-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              data-testid="snapshot-input-note"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-stone-700 hover:bg-stone-100 text-sm"
            data-testid="snapshot-cancel"
          >Cancel</button>
          <button
            onClick={onSave} disabled={saving}
            className="px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="snapshot-save"
          >
            {saving ? <ArrowsClockwise size={14} className="animate-spin" /> : <TrendDown size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
